import { useParams } from "react-router-dom";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { Paper } from "@mantine/core";
import { gql } from "@/__generated__";
import Navbar from "@/components/layout/Navbar/Navbar";
import { ReferPatientProgressTracker } from "@/components/ReferPatientProgressTracker";
import { StrengthenReferral } from "../StrengthenReferral/StrengthenReferral";
import { PageFooter } from "@/components/layout/PageFooter";
import InsuranceInfoForm, {
  InsuranceInfoFormHandle,
} from "@/components/InsuranceInfoForm/InsuranceInfoForm";
import styles from "../StrengthenReferral/styles/tab-content.module.scss";

export const GET_APPEAL_STATUS = gql(`
  query GetAppealStatus($appealId: ID!) {
    getAppealById(appealId: $appealId) {
      appealId
      status {
        referred
      }
    }
  }
`);

export function Insurance() {
  const { appealId } = useParams<{ appealId: string }>();
  const formRef = useRef<InsuranceInfoFormHandle>(null);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data, loading: statusLoading } = useQuery(GET_APPEAL_STATUS, {
    variables: { appealId: appealId! },
    skip: !appealId,
    fetchPolicy: "no-cache",
  });

  const readOnly = data?.getAppealById?.status?.referred ?? false;

  if (!appealId) {
    return null;
  }

  const handleSave = async (shouldNavigate = true) => {
    setIsSubmitting(true);
    try {
      const success = await formRef.current?.submit();
      if (success && shouldNavigate) {
        navigate(`/appeals/${appealId}/strengthen/documents`);
      }
      return success ?? false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveBeforeSend = async () => {
    return await handleSave(false);
  };

  const footer =
    !statusLoading && !readOnly ? (
      <PageFooter
        primaryButton={{
          text: "Save",
          onClick: handleSave,
          loading: isSubmitting,
          disabled: isSubmitting,
        }}
      />
    ) : undefined;

  return (
    <Navbar
      titleComponent={
        <ReferPatientProgressTracker
          appealId={appealId}
          onBeforeSendReferral={handleSaveBeforeSend}
        />
      }
      footer={footer}
    >
      <StrengthenReferral
        appealId={appealId}
        activeTab="insurance"
        tabContent={
          <Paper className={styles.container} p="md">
            <InsuranceInfoForm
              ref={formRef}
              appealId={appealId}
              readOnly={readOnly}
            />
          </Paper>
        }
      />
    </Navbar>
  );
}
